/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  SectionList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AuditLogDetailBody } from '../../components/AuditLogDetailBody';
import { ScreenHeroCard } from '../../../shared/surface-cards/ScreenHeroCard';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import type { AuditLogRow } from '../../../shell/audit/auditLogs';
import { formatAuditEventLabel } from '../../../shell/audit/auditLogs';
import {
  formatAuditWhen,
  getAuditEventIconName,
  getAuditListSubtitle,
} from '../../../shell/audit/auditLogPresentation';
import { spacing } from '../../../shared/theme/spacing'
import { webOperationalScrollBindings } from '../../../shared/theme/webScrollbar';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { TicketListRow } from '../../../shared/ticket/TicketListRow';
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { settingsStyles } from '../settingsScreen.styles';

export type AuditDaySection = { title: string; data: AuditLogRow[] };

type Props = {
  /** Omitir si el shell ya muestra el título de pantalla. */
  title?: string;
  lead: string;
  errorMessage?: string | null;
  sections: AuditDaySection[];
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  emptyLabel: string;
  selectedRow: AuditLogRow | null;
  onSelectRow: (row: AuditLogRow | null) => void;
  headerExtra?: React.ReactNode;
  heroActions?: React.ReactNode;
  desktopTable?: React.ReactNode;
  resolveUserLabel?: (userId: string | null) => string | null;
  customFooter?: React.ReactElement | null;
  /** Clean design mobile: flat header, flat rows with icon-wraps. */
  mobile?: boolean;
};

export function SettingsAuditListScreen({
  title,
  lead,
  errorMessage,
  sections,
  loading,
  refreshing,
  onRefresh,
  loadingMore,
  hasMore,
  onLoadMore,
  emptyLabel,
  selectedRow,
  onSelectRow,
  headerExtra,
  heroActions,
  desktopTable,
  resolveUserLabel,
  customFooter,
  mobile = false,
}: Props) {
  const { contentMaxWidth, layoutWebShell } = useContentLayout('settingsWide');

  const renderItem = ({ item }: { item: AuditLogRow }) => {
    const subtitle = getAuditListSubtitle(item);
    const icon = getAuditEventIconName(item.event_type);
    const isSelected = layoutWebShell && selectedRow?.id === item.id;
    const userLabel = resolveUserLabel?.(item.user_id) ?? null;

    if (mobile) {
      return (
        <View style={mobileStyles.row}>
          <View style={mobileStyles.iconWrap}>
            <Ionicons name={icon} size={16} color={ticketColors.ink2} />
          </View>
          <View style={mobileStyles.rowBody}>
            <Text style={mobileStyles.rowTitle} numberOfLines={1}>
              {formatAuditEventLabel(item.event_type)}
            </Text>
            <Text style={mobileStyles.rowTime} numberOfLines={1}>
              {formatAuditWhen(item.created_at)}
              {userLabel ? ` · ${userLabel}` : ''}
            </Text>
            {subtitle ? (
              <Text style={mobileStyles.rowSubtitle} numberOfLines={2}>
                {subtitle}
              </Text>
            ) : null}
          </View>
        </View>
      );
    }

    return (
      <View style={settingsStyles.auditRowWrap}>
        <TicketListRow
          title={formatAuditEventLabel(item.event_type)}
          subtitle={
            userLabel
              ? `${formatAuditWhen(item.created_at)} · ${userLabel}`
              : formatAuditWhen(item.created_at)
          }
          meta={subtitle}
          style={isSelected ? styles.rowSelected : undefined}
          onPress={layoutWebShell ? () => onSelectRow(isSelected ? null : item) : undefined}
          accessibilityLabel={formatAuditEventLabel(item.event_type)}
        />
      </View>
    );
  };

  const listHeader = mobile ? (
    <View style={mobileStyles.headerBlock}>
      <View style={mobileStyles.headerTopRow}>
        <View style={mobileStyles.headerText}>
          {title ? <Text style={mobileStyles.headerTitle}>{title}</Text> : null}
          <Text style={mobileStyles.headerLead}>{lead}</Text>
        </View>
        {heroActions ? <View style={mobileStyles.headerActions}>{heroActions}</View> : null}
      </View>
      {errorMessage ? <Text style={styles.errorText}>{errorMessage}</Text> : null}
      {headerExtra}
    </View>
  ) : (
    <View style={styles.headerBlock}>
      <ScreenHeroCard style={styles.heroCard}>
        <View style={styles.heroHeaderRow}>
          <View style={styles.heroHeaderText}>
            {title ? <Text style={settingsStyles.screenHeroTitle}>{title}</Text> : null}
            <Text style={settingsStyles.screenLead}>{lead}</Text>
          </View>
          {heroActions ? <View style={styles.heroActions}>{heroActions}</View> : null}
        </View>
      </ScreenHeroCard>
      {errorMessage ? <Text style={styles.errorText}>{errorMessage}</Text> : null}
      {headerExtra}
    </View>
  );

  const listFooter =
    loadingMore ? (
      <View style={{ paddingVertical: spacing.md, alignItems: 'center' }}>
        <ActivityIndicator color={ticketColors.gold} size="small" />
      </View>
    ) : hasMore && sections.some((s) => s.data.length > 0) ? (
      <TouchableOpacity style={settingsStyles.moreLinkBtn} onPress={onLoadMore} accessibilityRole="button">
        <Text style={settingsStyles.moreLinkText}>Cargar más</Text>
      </TouchableOpacity>
    ) : null;

  const sectionList = (
    <SectionList
      {...webOperationalScrollBindings([
        styles.list,
        !layoutWebShell && contentMaxWidth != null ? { maxWidth: contentMaxWidth, alignSelf: 'center' } : null,
      ])}
      contentContainerStyle={styles.listContent}
      sections={sections}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      ListHeaderComponent={listHeader}
      renderSectionHeader={({ section: { title } }) => (
        <Text style={mobile ? mobileStyles.sectionTitle : settingsStyles.listSectionTitle}>
          {title}
        </Text>
      )}
      stickySectionHeadersEnabled
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={[ticketColors.gold]}
          tintColor={ticketColors.gold}
        />
      }
      onEndReached={onLoadMore}
      onEndReachedThreshold={0.4}
      ListEmptyComponent={
        loading ? (
          <ModuleEmptyState loading loadingText="Cargando…" />
        ) : (
          <ModuleEmptyState title={emptyLabel} />
        )
      }
      ListFooterComponent={customFooter ?? listFooter}
    />
  );

  if (layoutWebShell && desktopTable) {
    return (
      <View style={styles.root}>
        <ScrollView
          style={styles.fullWidthScroll}
          contentContainerStyle={styles.fullWidthScrollContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[ticketColors.gold]}
              tintColor={ticketColors.gold}
            />
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {listHeader}
          {desktopTable}
        </ScrollView>
      </View>
    );
  }

  if (layoutWebShell) {
    return (
      <View style={styles.workspaceRoot}>
        <View style={styles.workspaceList}>{sectionList}</View>
        <View style={styles.workspaceDetail}>
          {selectedRow ? (
            <AuditLogDetailBody
              row={selectedRow}
              formatWhen={formatAuditWhen}
              onClose={() => onSelectRow(null)}
              inline
            />
          ) : (
            <View style={styles.workspacePlaceholder}>
              <Ionicons name="list-outline" size={32} color={ticketColors.ink3} />
              <Text style={settingsStyles.caption}>Seleccioná un evento para ver el detalle.</Text>
            </View>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      {sectionList}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: ticketColors.bg,
    alignItems: 'center',
  },
  workspaceRoot: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: ticketColors.bg,
    minHeight: 0,
  },
  workspaceList: {
    flex: 1,
    minWidth: 280,
    maxWidth: 420,
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: ticketColors.rule,
  },
  fullWidthScroll: {
    flex: 1,
    width: '100%',
  },
  fullWidthScrollContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xxl,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  workspaceDetail: {
    flex: 2,
    minWidth: 0,
    minHeight: 0,
  },
  workspacePlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.xl,
  },
  list: {
    flex: 1,
    width: '100%',
  },
  listContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xxl,
  },
  headerBlock: {
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    gap: spacing.sm,
  },
  heroCard: {
    marginBottom: 0,
  },
  heroHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  heroHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  heroActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 0,
  },
  errorText: {
    marginTop: spacing.sm,
    color: ticketColors.gold,
    fontSize: 14,
    lineHeight: 20,
  },
  rowSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
});

const mobileStyles = StyleSheet.create({
  headerBlock: {
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    gap: spacing.sm,
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: ticketColors.ink,
    letterSpacing: -0.3,
  },
  headerLead: {
    fontSize: 14,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 0,
    paddingTop: 2,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingTop: spacing.md,
    paddingBottom: 6,
    paddingLeft: 2,
    backgroundColor: ticketColors.bg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 2,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  rowTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  rowTime: {
    fontSize: 13,
    color: ticketColors.ink2,
  },
  rowSubtitle: {
    fontSize: 12,
    color: ticketColors.ink3,
    lineHeight: 17,
  },
});
